import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search, Plus, X } from "lucide-react";
import { Tile, Card, EmptyState, Loading, fmtN } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeoSearch({ headers, range, refreshKey, settings, saveSettings, onGoConnections }) {
  const [gsc, setGsc] = useState(null);
  const [newKw, setNewKw] = useState("");

  useEffect(() => {
    setGsc(null);
    axios
      .get(`${API}/admin/dashboard/gsc?range=${range}`, headers())
      .then((r) => setGsc(r.data))
      .catch(() => setGsc({ connected: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, refreshKey]);

  const keywords = settings?.tracked_keywords || [];
  const posMap = {};
  (gsc?.keywords || []).forEach((k) => { posMap[k.keyword] = k; });

  const addKw = async () => {
    const kw = newKw.trim().toLowerCase();
    if (!kw || keywords.includes(kw)) return;
    if (await saveSettings({ tracked_keywords: [...keywords, kw] })) setNewKw("");
  };
  const removeKw = (kw) => saveSettings({ tracked_keywords: keywords.filter((k) => k !== kw) });

  const sm = settings?.semrush;

  return (
    <div className="mt-6 space-y-5" data-testid="seo-tab">
      {/* GSC */}
      {!gsc ? (
        <Loading />
      ) : !gsc.connected ? (
        <EmptyState icon={Search} title="Connect Search Console" testid="gsc-empty-state">
          {gsc.error || "Google Search Console isn't connected yet, so clicks, impressions and query data can't load."}{" "}
          <button onClick={onGoConnections} data-testid="gsc-go-connections" className="text-[#E56A79] hover:text-white underline underline-offset-2 transition-colors">
            Open Connections for setup steps →
          </button>
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5">
            <Tile testid="gsc-clicks" label="Clicks" value={fmtN(gsc.totals.clicks)} sub={gsc.site_url} />
            <Tile testid="gsc-impressions" label="Impressions" value={fmtN(gsc.totals.impressions)} />
            <Tile testid="gsc-ctr" label="CTR" value={`${gsc.totals.ctr}%`} />
            <Tile testid="gsc-position" label="Avg Position" value={gsc.totals.position} />
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
            <Card title="Top Queries" testid="gsc-queries">
              <QueryTable rows={gsc.top_queries} keyLabel="Query" />
            </Card>
            <Card title="Top Pages" testid="gsc-pages">
              <QueryTable rows={gsc.top_pages} keyLabel="Page" trimUrl />
            </Card>
          </div>
        </>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Semrush snapshot */}
        <Card
          title="Semrush Snapshot"
          right={sm?.updated_at && <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-neutral-500">updated {sm.updated_at}</span>}
          testid="semrush-card"
        >
          {sm ? (
            <div className="space-y-4">
              {[
                { d: sm.primary_domain, k: sm.primary_keywords, t: sm.primary_traffic, main: true },
                { d: sm.legacy_domain, k: sm.legacy_keywords, t: sm.legacy_traffic, main: false },
              ].map((row) => (
                <div key={row.d} className="flex items-center justify-between gap-4 p-4 bg-[#121214] border border-white/5">
                  <div>
                    <div className={`text-[13px] font-semibold ${row.main ? "text-white" : "text-neutral-400"}`}>{row.d}</div>
                    {!row.main && <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-neutral-600 mt-0.5">Legacy domain</div>}
                  </div>
                  <div className="flex gap-6 text-right">
                    <div>
                      <div className="font-heading font-extrabold text-xl text-white">{fmtN(row.k)}</div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-neutral-500">keywords</div>
                    </div>
                    <div>
                      <div className="font-heading font-extrabold text-xl text-[#E56A79]">~{fmtN(row.t)}</div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.12em] text-neutral-500">est. visits/mo</div>
                    </div>
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-amber-400/80 leading-relaxed">{sm.note}</p>
              <p className="text-[10px] text-neutral-600">Edit these numbers in the Connections tab after each Semrush check.</p>
            </div>
          ) : (
            <p className="text-[13px] text-neutral-500">Loading settings…</p>
          )}
        </Card>

        {/* Tracked keywords */}
        <Card title="Tracked Keywords" right={<span className="font-mono text-[10px] uppercase tracking-[0.12em] text-neutral-500">{keywords.length} tracked</span>} testid="keywords-card">
          <div className="flex gap-2 mb-4">
            <input
              value={newKw}
              onChange={(e) => setNewKw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addKw()}
              placeholder="Add a keyword to track…"
              data-testid="keyword-input"
              className="flex-1 bg-[#121214] border border-white/10 focus:border-[#9D0423] focus:outline-none text-white text-[13px] px-3 py-2"
            />
            <button onClick={addKw} data-testid="keyword-add" className="inline-flex items-center gap-1.5 bg-[#9D0423] hover:bg-[#7E031C] text-white font-mono text-[10px] uppercase tracking-[0.12em] px-3.5 transition-colors">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="divide-y divide-white/5">
            {keywords.map((kw) => {
              const m = posMap[kw];
              return (
                <div key={kw} className="flex items-center justify-between gap-3 py-2.5" data-testid={`keyword-row-${kw.replace(/\s+/g, "-")}`}>
                  <span className="text-[13px] text-neutral-200">{kw}</span>
                  <span className="flex items-center gap-3">
                    {gsc?.connected ? (
                      m?.found ? (
                        <span className="font-mono text-[11px] text-white">
                          #{m.position} <span className="text-neutral-500">· {m.clicks} clicks · {fmtN(m.impressions)} impr</span>
                        </span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-neutral-600">no data in range</span>
                      )
                    ) : (
                      <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-neutral-600">connect GSC</span>
                    )}
                    <button onClick={() => removeKw(kw)} data-testid={`keyword-remove-${kw.replace(/\s+/g, "-")}`} aria-label={`Remove ${kw}`} className="text-neutral-600 hover:text-[#9D0423] transition-colors">
                      <X size={13} />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

function QueryTable({ rows, keyLabel, trimUrl = false }) {
  if (!rows?.length) return <p className="text-[13px] text-neutral-500">No data for this range yet.</p>;
  return (
    <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
      <table className="w-full text-left text-[12px]">
        <thead className="sticky top-0 bg-[#0D0D0F]">
          <tr>
            {[keyLabel, "Clicks", "Impr", "CTR", "Pos"].map((c) => (
              <th key={c} className="pb-2 pr-3 font-mono text-[9px] uppercase tracking-[0.14em] text-neutral-500 whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = r.keys[0] || "";
            const label = trimUrl ? key.replace(/^https?:\/\/[^/]+/, "") || "/" : key;
            return (
              <tr key={key} className="border-t border-white/5">
                <td className="py-2 pr-3 text-neutral-200 max-w-[240px] truncate" title={key}>{label}</td>
                <td className="py-2 pr-3 text-white font-semibold">{r.clicks}</td>
                <td className="py-2 pr-3 text-neutral-400">{fmtN(r.impressions)}</td>
                <td className="py-2 pr-3 text-neutral-400">{r.ctr}%</td>
                <td className="py-2 text-neutral-400">{r.position}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
